<script setup lang="ts">
import type { Coordinate } from 'ol/coordinate';
import { computed, ref } from 'vue';

import { GrammarBadge } from '@nora-monorepo/grammar-ui';
import type { NoraCoordinateFormat } from '@nora-monorepo/shared';
import { formatLatLongCoordinate, LatLong } from '@nora-monorepo/ui-shared';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';

interface Props {
  map: NoraMap;
  coordinateFormat: NoraCoordinateFormat;
  centerCoordinates: Coordinate | null;
}

const props = defineProps<Props>();
const isCopied = ref(false);
const formattedCoordinates = computed(() => {
  if (!props.centerCoordinates) {
    return '';
  }

  const latLong = LatLong.fromWebMercator(props.centerCoordinates);

  return formatLatLongCoordinate(latLong, props.coordinateFormat);
});

async function copyToClipboard() {
  if (!formattedCoordinates.value) {
    return;
  }

  try {
    await navigator.clipboard.writeText(formattedCoordinates.value);
    isCopied.value = true;
    setTimeout(() => isCopied.value = false, 1000);
  }
  catch (err) {
    console.error('Failed to copy coordinates:', err);
  }
}
</script>

<template>
  <GrammarBadge
    :label="formattedCoordinates"
    :bottom-label="isCopied ? 'Copied' : ''"
    style="cursor: pointer"
    size="lg"
    type="neutral"
    emphasis="default"
    :title="`${coordinateFormat} - Click to copy coordinates`"
    @click="copyToClipboard"
  />
</template>
